import { useEffect, useState, useCallback } from "react";
import PortalLayout from "@/layouts/PortalLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Plus, X } from "lucide-react";

export default function AdminUsers() {
  const [admins, setAdmins] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", first_name: "", last_name: "", password: "" });

  const load = useCallback(() => api.get("/admin/users/admins").then((r) => setAdmins(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/users/admins", form);
      toast.success("Admin créé");
      setForm({ email: "", first_name: "", last_name: "", password: "" });
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const demote = async (u) => {
    if (!window.confirm(`Retirer les droits admin de ${u.name} ? Cet utilisateur deviendra membre.`)) return;
    try {
      await api.patch(`/admin/users/${u.id}/role`, { role: "member" });
      toast.success("Droits retirés");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <PortalLayout sidebar="admin">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-navy md:text-4xl">Administrateurs</h1>
          <p className="mt-1 text-neutral-600">Gérez les comptes ayant les pleins pouvoirs sur la plateforme.</p>
        </div>
        <button onClick={() => setOpen(true)} className="inline-flex items-center gap-2 rounded-full bg-wine-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-wine-600" data-testid="create-admin-btn">
          <Plus className="h-4 w-4" /> Nouvel admin
        </button>
      </div>

      <div className="mt-8 space-y-3">
        {admins.map((u) => (
          <div key={u.id} className="flex items-center gap-4 rounded-2xl border border-neutral-100 bg-white p-4" data-testid={`admin-row-${u.id}`}>
            <div className="grid h-10 w-10 place-items-center rounded-full bg-wine-100 text-sm font-bold text-wine-700">
              {(u.first_name?.[0] || "A") + (u.last_name?.[0] || "")}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-navy">{u.name}</div>
              <div className="text-xs text-neutral-500">{u.email}</div>
            </div>
            <span className="inline-flex items-center gap-1 rounded-full bg-wine-50 px-2.5 py-1 text-xs font-bold text-wine-700"><ShieldCheck className="h-3 w-3" /> Admin</span>
            <button onClick={() => demote(u)} className="rounded-full bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100" data-testid={`demote-${u.id}`}>Retirer</button>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="admin-create-modal">
          <button onClick={() => setOpen(false)} className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm" />
          <form onSubmit={create} className="relative w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between border-b border-neutral-100 pb-3">
              <h2 className="text-lg font-bold text-navy">Créer un administrateur</h2>
              <button type="button" onClick={() => setOpen(false)} className="grid h-8 w-8 place-items-center rounded-full text-neutral-500 hover:bg-neutral-100"><X className="h-4 w-4" /></button>
            </div>
            <div className="mt-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="Prénom" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="new-admin-first-name" />
                <input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder="Nom" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="new-admin-last-name" />
              </div>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="new-admin-email" />
              <input required type="password" minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Mot de passe (min. 8 caractères)" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="new-admin-password" />
            </div>
            <button type="submit" className="mt-5 w-full rounded-full bg-wine-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-wine-600" data-testid="new-admin-submit">Créer</button>
          </form>
        </div>
      )}
    </PortalLayout>
  );
}
